<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue'
import { toRef } from 'vue'

import CampaignFormInviteSegments from './CampaignFormInviteSegments.vue'
import CampaignFormInviteUsers from './CampaignFormInviteUsers.vue'

import HelpOldIcon from '@/assets/icons/help-old.svg?component'
import InfoIcon from '@/assets/icons/information.svg?component'
import TagsList from '@/components/CampaignForm/components/CampaignFormAudience/TagsList.vue'
import { FLAG_SEGMENTS } from '@/lib/constants/flags'
import { type CampaignSegmentTarget, type InvitedUserType } from '@/types/campaign'
import type { UserTag } from '@/types/userTags'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  id: string
  isAllowedToInviteCreators: boolean
  userTags: UserTag[]
}>()
const hasInviteMetapicCreatorsEnable = defineModel<boolean>('hasInviteMetapicCreatorsEnable')
const hasApproveCreatorsEnable = defineModel<boolean>('hasApproveCreatorsEnable')
const tagIds = defineModel<number[]>('tagIds', { default: [] })
const invitedUsers = defineModel<InvitedUserType[]>('invitedUsers', { default: [] })
const segments = defineModel<CampaignSegmentTarget[]>('segments', { default: [] })
const isSegmentsEnabled = useFlag(FLAG_SEGMENTS)
const tagsFieldProp = toRef(props, 'userTags')

const handleAutoApprovalSwitch = (val: string | number | boolean) => {
  hasApproveCreatorsEnable.value = !!val
}
</script>

<template>
  <BoxComponent :id="id" :mb="32" :mb-lg="48" :p="24" class="campaign-form__block">
    <BoxComponent :mb="32">
      <TypographyComponent id="campaign-audience-section" variant="headline-medium">Audience</TypographyComponent>
    </BoxComponent>
    <BoxComponent v-if="isAllowedToInviteCreators" :mb="32">
      <FlexComponent justify="space-between" align="center">
        <FlexComponent align="center" :gap="8">
          <TypographyComponent variant="title-large">Invite 1000+ matching creators</TypographyComponent>
          <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
            <el-icon :size="16">
              <HelpOldIcon />
            </el-icon>
            <template #content>
              <BoxComponent :w="280">
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium" color="#000000"> Invite matching creators </TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium" color="#252423">
                  We'll match you with the most suitable creators from our database, considering your location and chosen categories.
                </TypographyComponent>
              </BoxComponent>
            </template>
          </el-tooltip>
        </FlexComponent>
        <el-switch v-model="hasInviteMetapicCreatorsEnable" size="large" @change="handleAutoApprovalSwitch" />
      </FlexComponent>
    </BoxComponent>
    <BoxComponent v-if="isAllowedToInviteCreators" :mb="16">
      <FlexComponent justify="space-between" align="center">
        <FlexComponent align="center" :gap="8">
          <TypographyComponent variant="title-large">Automatically approve creators after application</TypographyComponent>
          <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
            <el-icon :size="16">
              <HelpOldIcon />
            </el-icon>
            <template #content>
              <BoxComponent :w="280">
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium" color="#000000"> Auto-approval </TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium" color="#252423">
                  Invited creators can start promoting immediately after campaign creation. Turn off auto-approval if you prefer to approve
                  creators manually.
                </TypographyComponent>
              </BoxComponent>
            </template>
          </el-tooltip>
        </FlexComponent>

        <el-switch v-model="hasApproveCreatorsEnable" size="large" />
      </FlexComponent>
    </BoxComponent>
    <BoxComponent :mb="32">
      <BoxComponent :mb="16">
        <FlexComponent align="center" :gap="8">
          <TypographyComponent variant="title-large">Invite creators by email</TypographyComponent>
          <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
            <el-icon :size="16">
              <HelpOldIcon />
            </el-icon>
            <template #content>
              <BoxComponent :w="280">
                <BoxComponent :mb="4">
                  <TypographyComponent variant="title-medium" color="#000000"> Invite by email </TypographyComponent>
                </BoxComponent>
                <TypographyComponent variant="body-medium" color="#252423">
                  To work with specific creators, enter their email addresses separated by commas. We'll automatically send them an
                  invitation email with the campaign link.
                </TypographyComponent>
              </BoxComponent>
            </template>
          </el-tooltip>
        </FlexComponent>
      </BoxComponent>
      <CampaignFormInviteUsers v-model:invited-users="invitedUsers" />
    </BoxComponent>
    <BoxComponent v-if="isSegmentsEnabled" :mb="32">
      <CampaignFormInviteSegments v-model:segments="segments" />
    </BoxComponent>
    <BoxComponent v-if="tagsFieldProp.length">
      <TypographyComponent variant="title-large">Invite creator tags</TypographyComponent>
      <TagsList v-model:tag-ids="tagIds" :user-tags="tagsFieldProp" />
    </BoxComponent>
    <BoxComponent v-else class="campaign-form__notification-container" :p="16">
      <FlexComponent :gap="16">
        <el-icon :size="24">
          <InfoIcon />
        </el-icon>
        <TypographyComponent variant="body-large" class="campaign-form--notification">
          Your campaign will be created and you can invite creators later by using the <b>Manage</b> or <b>Edit</b> button or sharing the
          invite link.
        </TypographyComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.text-color {
  color: var(--primary-color);
}

b {
  font-weight: 500;
}

.campaign-fom__notification {
  color: #252423;
}
.campaign-form__notification-container {
  border-radius: 16px;
  border: 1px solid rgba(3, 30, 81, 0.12);
}
</style>
